import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Film, Users, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import RevealImage from "@/components/RevealImage";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projects = [
  {
    title: "HELENE: Ignoring The Red Tape",
    description: "This film dives deep into the aftermath of one of the most catastrophic storms to hit the region, where entire communities were wiped off the map and lives were forever changed.",
    image: "https://static.prod-images.emergentagent.com/jobs/99d8273e-5c16-489e-8838-f7d82f660229/images/97a2480cecb14b6a7554ba007564741ce32eefe369af76673ed2b01c4de07340.png",
    youtubeId: "_9qSuF98nm8",
    youtubeUrl: "https://www.youtube.com/watch?v=_9qSuF98nm8&t=647s",
    slug: "helene",
  },
  {
    title: "Senior Citizen Trafficking Is Real",
    description: "From financial exploitation to forced relocation and abuse, this documentary uncovers a side of trafficking most people don't even know exists.",
    image: "https://images.unsplash.com/photo-1643224196036-a61f69ff4144?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwcG9ydHJhaXQlMjBob3BlZnVsfGVufDB8fHx8MTc3NjI1NjY4M3ww&ixlib=rb-4.1.0&q=85",
    youtubeId: "ZlidR_eW7Go",
    youtubeUrl: "https://www.youtube.com/watch?v=ZlidR_eW7Go&t=414s",
    slug: "senior-trafficking",
  },
  {
    title: "COSMO CAN'T DIE",
    description: "From the moment his life changed forever to the day he stepped onto the Mr. Olympia stage as a professional wheelchair bodybuilder, this documentary follows his fight, his family's strength, and the relentless will to rise.",
    image: "/cosmo-photo.png",
    youtubeId: null,
    youtubeUrl: null,
    slug: "cosmo",
    comingSoon: true,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

export default function HomePage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribers`, { email });
      toast.success("Welcome! You're now subscribed.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-body" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center grain-overlay" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/99d8273e-5c16-489e-8838-f7d82f660229/images/1c5e7345e1f9201fa1edf050f7746f01ce7d08e60c28ce577ea73cd92df30fca.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-24 pb-16">
          <motion.div
            className="max-w-4xl"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              className="text-xs uppercase tracking-[0.25em] text-[#C86B53] font-semibold mb-6"
            >
              A Nonprofit Media Initiative
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none text-[#292524] mb-8"
            >
              Our mission is to make
              <br />
              the unseen <em className="text-[#C86B53]">seen</em>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-base sm:text-lg font-medium leading-relaxed text-[#57534E] max-w-2xl mb-10"
            >
              Visible exists to uncover, amplify, and share the stories that matter &mdash; the ones
              overlooked, untold, or forgotten. Through podcasts, documentaries, and creative
              collaborations, we transform invisible struggles into visible hope.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/films" data-testid="hero-watch-films-link">
                <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-8 h-11 text-sm font-semibold tracking-wide">
                  Watch Our Films
                  <ArrowRight size={16} strokeWidth={2} className="ml-1" />
                </Button>
              </Link>
              <Link to="/contact-us" data-testid="hero-contact-link">
                <Button
                  variant="outline"
                  className="border-[rgba(41,37,36,0.2)] text-[#292524] hover:bg-[#F2EDE4] rounded-sm px-8 h-11 text-sm font-semibold tracking-wide"
                >
                  Contact Us
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 lg:py-32 bg-[#F2EDE4]" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
                Our Vision
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-6">
                When light reaches the unseen, transformation follows
              </motion.h2>
              <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E] mb-8">
                The unseen doesn't stay unseen forever. We use storytelling and media to expose what's
                hidden, amplify voices that are often silenced, and support transformation where it's
                needed most.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                {[
                  { icon: Film, label: "Documentaries" },
                  { icon: Users, label: "Mentorship / Creator Support" },
                  { icon: Handshake, label: "Partnerships / Local Causes" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#C86B53]/10 flex items-center justify-center">
                      <Icon size={16} strokeWidth={2} className="text-[#C86B53]" />
                    </div>
                    <span className="text-sm font-semibold text-[#292524]">{label}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} className="mt-8">
                <Link to="/contact-us" data-testid="vision-contact-link">
                  <Button
                    variant="outline"
                    className="border-[rgba(41,37,36,0.2)] text-[#292524] hover:bg-white/50 rounded-sm px-6 h-10 text-sm font-semibold tracking-wide"
                  >
                    Contact Us
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            <RevealImage
              src="/vision-photo.png"
              alt="Visible Foundation on location"
              aspectRatio="aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 lg:py-32" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
              Our Work
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-16">
              Our Recent Projects
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, i) => (
              <motion.div
                key={project.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                data-testid={`project-card-${project.slug}`}
              >
                {/* Video embed or Coming Soon with image */}
                {project.youtubeId ? (
                  <div className="video-container rounded-sm overflow-hidden shadow-md">
                    <iframe
                      src={`https://www.youtube.com/embed/${project.youtubeId}`}
                      title={project.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                ) : project.image ? (
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-black">
                    <img src={project.image} alt={project.title} className="w-full h-full object-contain" />
                    <div className="absolute top-3 right-3">
                      <span className="bg-[#C86B53] text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm font-semibold">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-[#292524] flex items-center justify-center">
                    <div className="text-center px-6">
                      <p className="font-heading text-2xl font-bold text-white mb-2">{project.title}</p>
                      <span className="bg-[#C86B53] text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm font-semibold">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                )}
                <h3 className="font-heading text-xl font-bold text-[#292524] mt-5 mb-2">
                  {project.title}
                </h3>
                <p className="text-sm font-medium text-[#57534E] leading-relaxed mb-4">
                  {project.description}
                </p>
                {project.youtubeUrl ? (
                  <Link
                    to="/films"
                    className="text-sm font-semibold text-[#C86B53] hover:text-[#b55c46] inline-flex items-center gap-1 transition-colors"
                    data-testid={`project-watch-${project.slug}`}
                  >
                    Watch Now
                    <ArrowRight size={14} strokeWidth={2} />
                  </Link>
                ) : (
                  <span className="text-sm font-semibold text-[#57534E] inline-flex items-center gap-1">
                    Coming Soon
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Help Create Change / Donate CTA */}
      <section className="py-24 lg:py-32 bg-[#292524] relative grain-overlay" data-testid="donate-cta-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              Help Create <em className="text-[#C86B53]">Change</em>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base font-medium text-white/70 max-w-xl mx-auto mb-10">
              Every dollar goes toward production costs, creator support, and sharing stories that
              would otherwise remain unseen.
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href="https://www.paypal.com/ncp/payment/3KN4GBXUVPSZL"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-donate-button"
              >
                <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-10 h-12 text-sm font-semibold tracking-wide">
                  Donate Now
                  <ArrowRight size={16} strokeWidth={2} className="ml-1" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 lg:py-32" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
                Stay Connected
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#292524] mb-4">
                Subscribe to Our Newsletter
              </motion.h2>
              <motion.p variants={fadeUp} className="text-sm font-medium text-[#57534E] mb-8">
                Get updates on new releases and upcoming crowdfunding opportunities.
              </motion.p>
              <motion.form
                variants={fadeUp}
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-3"
                data-testid="newsletter-form"
              >
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 bg-white border-[rgba(41,37,36,0.12)] rounded-sm flex-1 font-body text-sm"
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-8 h-11 text-sm font-semibold tracking-wide"
                  data-testid="newsletter-submit-button"
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                </Button>
              </motion.form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
